// src/ProductPage/ProductPage.tsx
import React from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";

import ProductMedia from "./ProductMedia";
import ProductDetails from "./ProductDetails";
import YouMayAlsoLike from "./YouMayAlsoLike";
import { PRODUCT_BY_SLUG_QUERY } from "../graphql/products";
import SchemaMarkup from "../components/SchemaMarkup";
import ProductFaqSection from "../components/ProductFaqSection";
import ProductIntroSection from "../components/ProductIntroSection";

type BackendProduct = {
  id: string;
  slug?: string | null;
  altText?: string | null;
  customSchema?: string | null;
  name: string;
  productType: string;
  shortDescription?: string | null;
  longDescription?: string | null;
  price: number;
  galleryImages?: string[] | null;
  rotationImages?: string[] | null;
};

type LocationState = {
  product?: BackendProduct;
};

type ProductByIdData = {
  product: BackendProduct | null;
};

const ProductPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const productFromState = state?.product ?? null;

  const { data, loading, error } = useQuery<ProductByIdData>(
    PRODUCT_BY_SLUG_QUERY,
    {
      variables: { slug: slug as string },
      skip: !slug,
      fetchPolicy: "cache-and-network",
    },
  );

  const product: BackendProduct | null =
    data?.product || productFromState || null;

  if (!slug) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Product not found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The requested product link is invalid.
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!product && loading) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!product && !loading) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Product not found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We couldn’t load details for this product.
          </Typography>
          {error && (
            <Box sx={{ mt: 2, maxWidth: 480, mx: "auto" }}>
              <Alert severity="error">
                {error.message ||
                  "An error occurred while loading the product."}
              </Alert>
            </Box>
          )}
        </Box>
      </Container>
    );
  }

  return (
    <>
      <SchemaMarkup schema={product?.customSchema} />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 6 } }}>
        <Grid container spacing={{ xs: 3, md: 6 }}>
          {/* LEFT: Media */}
          <Grid item xs={12} md={7}>
            <ProductMedia product={product!} />
          </Grid>

          {/* RIGHT: Product details */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ProductDetails product={product!} />
          </Grid>
        </Grid>
      </Container>
      <YouMayAlsoLike product={product!} />
      <ProductIntroSection productId={product!.id} />
      <ProductFaqSection productId={product!.id} productName={product!.name} />
    </>
  );
};

export default ProductPage;
